import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  TextField,
  Button,
  Grid,
  Divider,
  Paper,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import apiClient from './Api.js';

function Variables() {
  // Notification state
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [loading, setLoading] = useState(true);
  const [companyVariables, setCompanyVariables] = useState([]);
  
  const [isEditingVariables, setIsEditingVariables] = useState(false);
  const [originalVariables, setOriginalVariables] = useState([]);
  const [openAddVarDialog, setOpenAddVarDialog] = useState(false);
  const [newVariable, setNewVariable] = useState({
    name: '',
    value: '',
    unit: '%',
    frequency: 'Annual',
    description: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
        const response = await apiClient.getCompanyVariables();
        const data = Array.isArray(response) ? response : response.results || [];
      setCompanyVariables(data);
    } catch (error) {
      showNotification('Failed to load variables: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Show notification
  const showNotification = (message, severity = 'success') => {
    setNotification({
      open: true,
      message,
      severity
    });
  };

  // Handle notification close
  const handleCloseNotification = () => {
    setNotification({
      ...notification,
      open: false
    });
  };

  // Company Variables Functions
  const handleEditVariables = () => {
    setOriginalVariables(JSON.parse(JSON.stringify(companyVariables)));
    setIsEditingVariables(true);
  };

  const handleCancelVariables = () => {
    setCompanyVariables(JSON.parse(JSON.stringify(originalVariables)));
    setIsEditingVariables(false);
  };

  const handleSaveVariables = async () => {
    try {
      await apiClient.bulkUpdateVariables(companyVariables);
      setIsEditingVariables(false);
      showNotification('Company variables saved successfully');
    } catch (error) {
      showNotification('Failed to save variables: ' + error.message, 'error');
    }
  };

  const handleVariableChange = (id, field) => (event) => {
    const updatedVariables = companyVariables.map(variable => {
      if (variable.id === id) {
        return { ...variable, [field]: event.target.value };
      }
      return variable;
    });
    setCompanyVariables(updatedVariables);
  };

  const handleDeleteVariable = async (id) => {
    try {
      await apiClient.deleteCompanyVariable(id);
      setCompanyVariables(companyVariables.filter(variable => variable.id !== id));
      showNotification('Variable removed');
    } catch (error) {
      showNotification('Failed to delete variable: ' + error.message, 'error');
    }
  };

  const handleAddVariable = async () => {
    // Validate form
    if (!newVariable.name || !newVariable.value) {
      showNotification('Please fill in all required fields', 'error');
      return;
    }
  
    try {
      const createdVariable = await apiClient.createCompanyVariable(newVariable);
      setCompanyVariables([...companyVariables, createdVariable]);
      setOpenAddVarDialog(false);
      
      // Reset form
      setNewVariable({
        name: '',
        value: '',
        unit: '%',
        frequency: 'Annual',
        description: ''
      });
      
      showNotification('New variable added successfully');
    } catch (error) {
      showNotification('Failed to add variable: ' + error.message, 'error');
    }
  };

  const handleNewVariableChange = (field) => (event) => {
    setNewVariable({
      ...newVariable,
      [field]: event.target.value
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Company Variables Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" sx={{ color: '#1976d2' }}>Company Variables</Typography>
          {!isEditingVariables ? (
            <Button 
              variant="outlined" 
              startIcon={<EditIcon />} 
              onClick={handleEditVariables}
            >
              Edit
            </Button>
          ) : null}
        </Box>

        <Paper elevation={isEditingVariables ? 2 : 0} sx={{ p: isEditingVariables ? 3 : 0 }}>
          <Grid container spacing={2}>
            {companyVariables.map((variable) => (
              <Grid item xs={12} sm={6} key={variable.id}>
                <Box sx={{ 
                  p: 2, 
                  border: 1, 
                  borderColor: 'divider',
                  borderRadius: 1,
                  bgcolor: 'background.default'
                }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="subtitle1" fontWeight="medium">{variable.name}</Typography>
                    {isEditingVariables && (
                      <IconButton 
                        color="error" 
                        size="small" 
                        onClick={() => handleDeleteVariable(variable.id)}
                        sx={{ ml: 1 }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    {isEditingVariables ? (
                      <TextField
                        value={variable.value}
                        onChange={handleVariableChange(variable.id, 'value')}
                        variant="outlined"
                        size="small"
                        sx={{ width: '120px', mr: 1 }}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">{variable.unit}</InputAdornment>,
                        }}
                      />
                    ) : (
                      <Typography variant="h6" sx={{ color: '#4caf50', fontWeight: 'bold' }}>
                        {variable.value}{variable.unit}
                      </Typography>
                    )}
                    <Chip 
                        label={variable.frequency} 
                        size="small" 
                        color="primary"
                        variant="outlined"
                        sx={{ ml: 1 }} 
                      />
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary">
                    {variable.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          {isEditingVariables && (
            <>
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="contained"
                color="success"
                startIcon={<AddIcon />}
                onClick={() => setOpenAddVarDialog(true)}
              >
                Add New Variable
              </Button>

              </Box>

              <Divider sx={{ my: 3 }} />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleCancelVariables}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveVariables}
                  color="primary"
                >
                  Save Variables
                </Button>
              </Box>
            </>
          )}
        </Paper>
      </Box>

      {/* Add Variable Dialog */}
      <Dialog 
        open={openAddVarDialog} 
        onClose={() => setOpenAddVarDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add New Variable</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Variable Name"
                value={newVariable.name}
                onChange={handleNewVariableChange('name')}
                variant="outlined"
                required
              />
            </Grid>
            
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Value"
                value={newVariable.value}
                onChange={handleNewVariableChange('value')}
                variant="outlined"
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Select
                        value={newVariable.unit}
                        onChange={handleNewVariableChange('unit')}
                        variant="standard"
                        disableUnderline
                        sx={{ '& .MuiSelect-select': { p: 0, border: 'none' } }}
                      >
                        <MenuItem value="%">%</MenuItem>
                        <MenuItem value="$">$</MenuItem>
                        <MenuItem value="days">days</MenuItem>
                      </Select>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Frequency</InputLabel>
                <Select
                  value={newVariable.frequency}
                  label="Frequency"
                  onChange={handleNewVariableChange('frequency')}
                >
                  <MenuItem value="Annual">Annual</MenuItem>
                  <MenuItem value="Monthly">Monthly</MenuItem>
                  <MenuItem value="Weekly">Weekly</MenuItem>
                  <MenuItem value="Daily">Daily</MenuItem>
                  <MenuItem value="One-time">One-time</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={newVariable.description}
                onChange={handleNewVariableChange('description')}
                variant="outlined"
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddVarDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleAddVariable} 
            variant="contained" 
            color="primary"
          >
            Add Variable
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification */}
      <Snackbar 
        open={notification.open} 
        autoHideDuration={6000} 
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity} 
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Variables;